import React from 'react';

import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { TextComponent } from 'design/text/textComponents';
import { FloatingLabelComponent } from 'design/floatingLabel';
import { TextInputControlComponent } from 'design/textInputControl';
import { EmailFormValues, EmailFormViolation } from 'types/emailFormTypes';
import { INPUT_EMAIL_MAX_LENGTH } from 'utils/constants';
import { FieldsetLayoutComponent } from 'design/fieldsetLayout';

type Props = {
  disabled?: boolean;
  values: EmailFormValues;
  violations: EmailFormViolation[];
  onEmailValueChange(nextValue: string): void;
};

export const EmailFieldset = (props: Props) => {
  const { disabled, values, violations, onEmailValueChange } = props;

  const intl = useIntlDictionary();

  const emailHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'EmailMissed':
          return intl.inputValidationRequired;
        case 'EmailMalformed':
          return intl.inputValidationEmail;
        case 'UserNotFound':
          return intl.inputValidationEmailNotFound;
      }
    }
  }, [
    intl.inputValidationEmail,
    intl.inputValidationEmailNotFound,
    intl.inputValidationRequired,
    violations,
  ]);

  return (
    <FieldsetLayoutComponent>
      <FloatingLabelComponent
        lifted={!!values.email}
        invalid={violations.length > 0}
        titleText={
          <TextComponent color="secondary" size="m">
            {intl.emailInputPlaceholder}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {emailHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="email"
          autoFocus
          value={values.email}
          disabled={disabled}
          invalid={violations.length > 0}
          maxLength={INPUT_EMAIL_MAX_LENGTH}
          onValueChange={onEmailValueChange}
        />
      </FloatingLabelComponent>
    </FieldsetLayoutComponent>
  );
};
